import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { gptAPI } from 'services/gptService';
import { GPTAdditionalSettings, GptEngine, GPTOptionParams, IGPTRule, IGPTRuleExtended } from './types';
import { createNullArray } from '../markupRules/actions';
import { defaultEngine } from './constants';

interface gptState {
	gptRules: (IGPTRule | null)[];
	gptRulesLoading: boolean;
	currentRule: IGPTRuleExtended | null;
	isNewRule: boolean;
	gptEngines: GptEngine[];
	gptEnginesLoading: boolean;
	gptModels: string[];
	currentGptEngine: GptEngine;
	defaultGptModel: string;
	gptModelsLoading: boolean;
	currentRuleLoading: boolean;
	gptAdditionalParams: Record<string, GPTOptionParams>;
}

const initialState: gptState = {
	gptRules: createNullArray(5),
	gptRulesLoading: true,
	currentRule: null,
	isNewRule: false,
	gptEngines: [],
	gptEnginesLoading: false,
	gptModels: [],
	currentGptEngine: defaultEngine,
	defaultGptModel: '',
	gptModelsLoading: false,
	currentRuleLoading: false,
	gptAdditionalParams: {},
};

const baseSettings = {
	engine: defaultEngine,
	model: '',
}

export const defaultGPTSettings: GPTAdditionalSettings = {
	...baseSettings,
	assistantText: '',
}

const newGptRule: IGPTRuleExtended = {
	...baseSettings,
	title: '',
	simpleQuestions: [],
}

export const gptSlice = createSlice({
	name: 'chatGPT',
	initialState,
	reducers: {
		setNewRule(state, action: PayloadAction<Partial<IGPTRuleExtended> | null>) {
			state.currentRule = action.payload ? { ...newGptRule, ...action.payload } : newGptRule;
			state.isNewRule = true;
		},
		removeNewRuleState(state) {
			state.isNewRule = false;
		},
		resetCurrentRule(state) {
			state.currentRule = null;
		},
	},
	extraReducers: (builder) => {
		builder.addMatcher(
			gptAPI.endpoints.getGPTRules.matchPending,
			(state) => {
				state.gptRulesLoading = true;
			},
		);
		builder.addMatcher(
			gptAPI.endpoints.getGPTRules.matchFulfilled,
			(state, { payload }) => {
				state.gptRules = payload;
				state.gptRulesLoading = false;
			},
		);
		builder.addMatcher(
			gptAPI.endpoints.getGPTRules.matchRejected,
			(state) => {
				state.gptRulesLoading = false;
			},
		);
		builder.addMatcher(
			gptAPI.endpoints.getGPTRuleById.matchFulfilled,
			(state, { payload }) => {
				state.currentRule = payload;
			},
		);
		builder.addMatcher(
			gptAPI.endpoints.getGPTEngines.matchPending,
			(state) => {
				state.gptEnginesLoading = true;
			},
		);
		builder.addMatcher(
			gptAPI.endpoints.getGPTEngines.matchFulfilled,
			(state, { payload }) => {
				state.gptEngines = payload;
				state.gptEnginesLoading = false;
			},
		);
		builder.addMatcher(
			gptAPI.endpoints.getGPTEngines.matchRejected,
			(state) => {
				state.gptEnginesLoading = false;
			},
		);
		builder.addMatcher(
			gptAPI.endpoints.getGPTOptions.matchPending,
			(state) => {
				state.gptModelsLoading = true;
			},
		);
		builder.addMatcher(
			gptAPI.endpoints.getGPTOptions.matchFulfilled,
			(state, { payload }) => {
				const {
					models,
					defaultModel: defaultGptModel,
					additionalParams,
					currentGptEngine,
				} = payload;
				state.gptModels = models;
				state.defaultGptModel = defaultGptModel;
				state.currentGptEngine = currentGptEngine;
				state.gptAdditionalParams = additionalParams;
				state.gptModelsLoading = false;
			},
		);
		builder.addMatcher(
			gptAPI.endpoints.getGPTOptions.matchRejected,
			(state) => {
				state.gptModelsLoading = false;
			},
		);
		builder.addMatcher(
			gptAPI.endpoints.getGPTRuleById.matchPending,
			(state) => {
				state.currentRuleLoading = true;
			},
		);
		builder.addMatcher(
			gptAPI.endpoints.getGPTRuleById.matchFulfilled,
			(state) => {
				state.currentRuleLoading = false;
			},
		);
		builder.addMatcher(
			gptAPI.endpoints.getGPTRuleById.matchRejected,
			(state) => {
				state.currentRuleLoading = false;
			},
		);
	},
});

export default gptSlice.reducer;
